import { useState } from 'react'
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js'
import * as stripeJs from '@stripe/stripe-js'
import styled from '@emotion/styled'
import { ContinueButton } from './StyledComponents'
import GrooverText from './GrooverText'
import iconArrow from '/public/iconArrow.svg'
import Box from './Box'
import { EIGHT_PX, SIXTEEN_PX, EIGHTEEN_PX } from '../enums/Spacings'
import { getGrooverText } from '../libs/GrooverTextRepository'

const ErrorContainer = styled(Box)`
  color: #d32f2f;
  font-size: 14px;
  text-align: center;
  padding: ${EIGHT_PX} ${SIXTEEN_PX};
  margin-top: ${EIGHT_PX};
  background-color: rgba(211, 47, 47, 0.08);
  border-radius: 4px;
`

/**
 * Interface for Stripe payment errors.
 * Used by getStripeErrorMessage to extract error details.
 */
export interface StripePaymentError {
  code?: string
  decline_code?: string
  message?: string
}

/**
 * Maps Stripe error codes to translation keys.
 * Returns null for unknown error codes (so the caller can fall back to Stripe's message).
 *
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5
 */
export const getStripeErrorTranslationKey = (code?: string, declineCode?: string): string | null => {
  // Handle specific decline codes first
  if (code === 'card_declined' && declineCode) {
    switch (declineCode) {
      case 'insufficient_funds':
        return 'stripePaymentErrorInsufficientFunds'
      case 'live_mode_test_card':
        return 'stripePaymentErrorTestCard'
      default:
        return 'stripePaymentErrorCardDeclined'
    }
  }

  // Handle general error codes
  switch (code) {
    case 'card_declined':
      return 'stripePaymentErrorCardDeclined'
    case 'expired_card':
      return 'stripePaymentErrorExpiredCard'
    case 'incorrect_cvc':
      return 'stripePaymentErrorIncorrectCvc'
    case 'processing_error':
      return 'stripePaymentErrorProcessing'
    default:
      return null
  }
}

/**
 * Maps Stripe error codes to user-friendly translated messages.
 * Falls back to Stripe's message if no translation exists.
 *
 * Requirements: 4.6, 4.7
 */
export const getStripeErrorMessage = (error: StripePaymentError): string => {
  const { code, decline_code, message } = error

  // Try to get translated message based on error code
  const errorKey = getStripeErrorTranslationKey(code, decline_code)
  if (errorKey) {
    const translated = getGrooverText(errorKey)
    if (translated && translated !== errorKey) {
      return translated
    }
  }

  // Fall back to Stripe's message
  if (message) {
    return message
  }

  // Fall back to generic message
  const genericMessage = getGrooverText('stripePaymentErrorGeneric')
  if (genericMessage && genericMessage !== 'stripePaymentErrorGeneric') {
    return genericMessage
  }

  return 'Payment failed. Please try again.'
}

type Props = {
  onIsPaid: (isPaid) => void
}

const StripeCheckoutForm = ({ onIsPaid }: Props) => {
  const stripe = useStripe()
  const elements = useElements()

  const [errorMessage, setErrorMessage] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (event) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault()

    // Clear any previous error message when starting a new payment attempt
    setErrorMessage('')

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return
    }

    setLoading(true)

    const returnUrl = `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/pay-success`

    const { error } = await stripe.confirmPayment({
      elements, // `Elements` instance that was used to create the Payment Element
      confirmParams: {
        return_url: returnUrl
      },
      redirect: 'if_required'
    })

    if (error) {
      setLoading(false)
      // This point will only be reached if there is an immediate error when
      // confirming the payment. Show error to your customer (for example, payment
      // details incomplete)
      setErrorMessage(getStripeErrorMessage(error))
    } else {
      // Your customer will be redirected to your `return_url`. For some payment
      // methods like iDEAL, your customer will be redirected to an intermediate
      // site first to authorize the payment, then redirected to the `return_url`.
      // Keep loading=true until parent unmounts this form so the pay control cannot flash enabled.
      onIsPaid(true)
    }
  }

  const paymentElementOptions: stripeJs.StripePaymentElementOptions = {
    layout: {
      type: 'accordion',
      defaultCollapsed: false,
      radios: true,
      spacedAccordionItems: false
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Box fullWidth>
        <PaymentElement options={paymentElementOptions} />

        {/* Error display */}
        {errorMessage && <ErrorContainer>{errorMessage}</ErrorContainer>}

        <Box alignSelf="flex-end" top spacing={EIGHTEEN_PX} gap={EIGHT_PX}>
          <ContinueButton type="submit" isDisabled={!stripe || loading} disabled={!stripe || loading}>
            <GrooverText textKey="registrationCtactaContinue" />
            <img src={iconArrow} width={16} height={16} />
          </ContinueButton>
        </Box>
      </Box>
    </form>
  )
}

export default StripeCheckoutForm
